<script setup lang="ts">
	import { useCefStore } from '@/stores/cef'
	import { useHotelsStore } from '@/stores/hotels'
	import { useReservationStore } from '@/stores/reservation'
	import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
	import { TransitionRoot } from '@headlessui/vue'
	import {
		useElementBounding,
		useElementVisibility,
		useWindowSize,
	} from '@vueuse/core'
	import { storeToRefs } from 'pinia'
	import { computed, onBeforeUnmount, provide, ref, watch } from 'vue'
	import { useRoute } from 'vue-router'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import ErrorBoundary from '@/components/ErrorBoundary.vue'
	import StyledAlertList from '@/components/StyledAlertList.vue'

	import useAuthUser from '@/composables/useAuthUser.ts'

	import { OrderConditions } from '@/enums/orderConditions'

	import HotelEntry from '@/modules/hotels/components/HotelEntry.vue'
	import LoadMoreButton from '@/modules/hotels/components/LoadMoreButton.vue'
	import ViewSectionToggle from '@/modules/hotels/components/ViewSectionToggle.vue'
	import useHotels from '@/modules/hotels/composables/useHotels'
	import useFlightRequestDetail from '@/modules/planes/composables/useFlightRequestDetail'
	import { useCefRequests } from '@/modules/trips/composables/useCefsRequests'
	import type { FlightRequestDetail } from '@/modules/trips/interfaces/tripDetail'

	import AvailabilityFilters from '../components/AvailabilityFilters.vue'
	import AvailabilityForm from '../components/AvailabilityForm.vue'
	import AvailabilityInteractiveInformation from '../components/AvailabilityInteractiveInformation.vue'
	import AvailabilityModalDisclaimer from '../components/AvailabilityModalDisclaimer.vue'
	import AvailabilitySummarySearch from '../components/AvailabilitySummarySearch.vue'
	import LoadingHotels from '../components/LoadingHotels.vue'
	import NoSearchedHotelsMessage from '../components/NoSearchedHotelsMessage.vue'
	import RecentHotelSearches from '../components/RecentHotelSearches.vue'
	import TravelerConflictAlert from '../components/TravelerConflictAlert.vue'
	import type { Traveler } from '../interfaces/Traveler'
	import type { Hotel } from '../interfaces/hotel'
	import type { RecentHotelSearch } from '../interfaces/recentHotelSearch'

	// Page state
	const expandHotelForm = ref(true)
	const disclaimerIsOpen = ref(false)
	const actualDisclaimer = ref('')
	const selectedPeople = ref<Traveler[]>([])
	const inputDiv = ref({ el: null })
	const query = ref('')
	const travelers = ref<string[]>([])
	const displayHotels = ref(true)
	const displayMap = ref(false)
	const isMobile = ref(false)
	const listSectionRef = ref(null)
	// Todo lo que vive arriba del mapa/lista (breadcrumb, alerta de conflicto,
	// form de búsqueda, barra colapsada) — medido en vivo en vez de asumir un
	// número fijo, porque su alto real cambia: el banner de políticas
	// infringidas, por ejemplo, solo aparece a veces. Un offset a mano se
	// desincroniza apenas algo de esto cambia de alto; esto no.
	const aboveMapRef = ref<HTMLElement | null>(null)
	// windowScroll: false — el default hace un getBoundingClientRect() en
	// cada evento de scroll de la ventana. aboveMapHeight no cambia al
	// scrollear (el breadcrumb/form/barra de arriba no se mueven de alto por
	// eso), así que esos recalculos son puro costo sobre una pantalla que ya
	// tiene un canvas WebGL (el mapa) de por medio.
	const { height: aboveMapHeightRaw } = useElementBounding(aboveMapRef, {
		windowScroll: false,
	})
	// La columna de lista y la del mapa calculan cada una su propio
	// `calc(100dvh - 5rem - aboveMapHeight px)` por separado (ver el porqué en
	// AvailabilityInteractiveInformation.vue) — si aboveMapHeight trae
	// decimales, el navegador puede redondear cada calc() para un lado
	// distinto y dejar un residuo de scroll fantasma en el body. Redondeando
	// hacia arriba ambas quedan con el mismo entero.
	//
	// + PT_ABOVE_MAP_PX: aboveMapRef mide breadcrumb + form + barra de
	// búsqueda, pero el pt-5 (padding-top) del contenedor que envuelve la
	// fila lista/mapa vive AFUERA de aboveMapRef — nunca se mide, así que
	// nunca se restaba, aunque sí empuja la fila hacia abajo. A diferencia
	// del contenido de aboveMapRef (que cambia con el banner de políticas,
	// el breakpoint, etc.), este pt-5 es una clase de Tailwind estática: no
	// hace falta medirlo, alcanza con conocer su valor.
	const PT_ABOVE_MAP_PX = 20 // pt-5
	const aboveMapHeight = computed(
		() => Math.ceil(aboveMapHeightRaw.value) + PT_ABOVE_MAP_PX
	)
	const extraInformationRef = ref(null)
	const hotelListRef = ref(null)
	const interactiveInfoRef = ref<InstanceType<
		typeof AvailabilityInteractiveInformation
	> | null>(null)
	const animateHotelList = ref(false)
	const itemRefs = ref<
		{ hotelId: string; hotelContainer: HTMLElement | null }[]
	>([])

	const {
		filteredList: filteredHotelList,
		form,
		hotels,
		isFetching,
		isError,
		moreIndicator,
		selectedHotel,
		reservationNights,
		policyInformation,
		orderBy,
		favoritePriceOrder,
		setOrderBy,
		setFavoritePriceOrder,
		resetValues,
		setSelectedHotel,
		pushToDetails,
		searchHotels,
		errorMessage,
		searchQuery,
	} = useHotels()

	// Cambiar destino/estancia desde AvailabilitySummarySearch (sin abrir el
	// form completo) dispara la búsqueda vía OTRA instancia de useHotels(); no
	// se puede confiar en que su propio isFetching se sincronice con el de
	// esta página (ya falló una vez), así que se controla con esta bandera
	// explícita del store para bloquear/mostrar loading en la lista.
	const { isQuickSearching, hotelPreferences, searchContext } =
		storeToRefs(useHotelsStore())

	// Ensure orderBy is properly typed
	const typedOrderBy = computed(() => orderBy.value as OrderConditions)

	const availabilityFormRef = ref<InstanceType<typeof AvailabilityForm> | null>(
		null
	)

	const { user } = useAuthUser()

	const cefStore = useCefStore()
	const reservationStore = useReservationStore()

	const route = useRoute()
	const linkedCefId = computed(() =>
		route.query.linkedCefId ? Number(route.query.linkedCefId) : null
	)

	cefStore.reset()
	if (!linkedCefId.value) reservationStore.resetReservation()

	const { data: linkedFlight } = useFlightRequestDetail(linkedCefId)

	const { requests: linkedCefRequests } = useCefRequests({
		params: { cefId: linkedCefId.value ?? 0 },
		immediate: !!linkedCefId.value,
	})

	const closeModal = () => {
		disclaimerIsOpen.value = false
	}

	const openDisclaimerModal = (disclaimer: string) => {
		actualDisclaimer.value = disclaimer
		disclaimerIsOpen.value = true
	}

	const resetGuestQuery = () => {
		query.value = ''
		inputDiv.value = { el: null }
	}

	watch(selectedPeople, (currentValue) => {
		const selectedPeopleIds = currentValue.map((element) => {
			return element.id
		})

		travelers.value = selectedPeopleIds

		resetGuestQuery()
	})

	const { width } = useWindowSize()

	const extraInformationIsVisible = useElementVisibility(extraInformationRef)
	const listSectionIsVisible = useElementVisibility(listSectionRef)

	const showMap = () => {
		displayMap.value = true

		if (isMobile.value) {
			displayHotels.value = false
		}
	}

	const hasSpecificErrors = computed(() => {
		if (!errorMessage.value?.errors) return false

		const specificErrorKeys = Object.keys(errorMessage.value.errors).filter(
			(key) => key !== 'users'
		)

		return specificErrorKeys.some((key) => {
			const errorList = errorMessage.value.errors[key]
			return Array.isArray(errorList) && errorList.length > 0
		})
	})

	const scrollToTargetAdjusted = (element: HTMLElement | null) => {
		if (element) {
			const headerOffset = 64
			const elementPosition = element.getBoundingClientRect().top
			const offsetPosition = elementPosition + window.scrollY - headerOffset

			window.scrollTo({
				top: offsetPosition,
				left: 0,
				behavior: 'smooth',
			})
		}
	}

	const moveToList = (element: HTMLElement | null) => {
		if (element) {
			scrollToTargetAdjusted(element)
		}
	}

	const moveToBottom = () => {
		const element = document.body

		element.scrollIntoView({ block: 'end', behavior: 'smooth' })
	}

	const toggleSections = () => {
		displayHotels.value = !displayHotels.value
		displayMap.value = !displayMap.value

		if (displayMap.value) {
			setTimeout(() => {
				moveToBottom()
			}, 200)
		}
	}

	const defineClientType = () => {
		isMobile.value = width.value < 1250
	}

	const evaluateLayout = () => {
		if (isMobile.value && displayMap.value && displayHotels.value) {
			displayMap.value = false
			displayHotels.value = true
		}

		if (!isMobile.value && displayMap.value && !displayHotels.value) {
			displayHotels.value = true
		}
	}

	const centerActiveHotel = (hotel: Hotel) => {
		if (interactiveInfoRef.value) {
			interactiveInfoRef.value.centerActiveHotel(hotel)
		}
	}

	const setNewRequest = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
		expandHotelForm.value = true
	}

	const pages = [
		{
			name: 'Reservación hotelera',
			to: { name: 'hotel.availability' },
			current: true,
		},
	]

	const setExpandHotelForm = (value: boolean) => {
		expandHotelForm.value = value
	}

	// «Últimas búsquedas» ocupa el lugar de los resultados mientras no los hay: con el
	// formulario abierto, y también con el formulario colapsado (la flecha) si no hay
	// hoteles que mostrar. Con resultados en pantalla no: ahí manda la lista y el mapa.
	// Los internos no tienen historial: sus búsquedas a nombre de un cliente no se registran.
	const showRecentSearches = computed(
		() =>
			!isFetching.value &&
			!isQuickSearching.value &&
			!user.value?.internal &&
			(expandHotelForm.value || isError.value || !hotels.value?.length)
	)

	/**
	 * Llena el formulario con una búsqueda anterior y, con `submit`, la vuelve a lanzar.
	 * Si se eligió con el formulario colapsado, se abre para que se vea lo que se llenó
	 * (o lo que falta, si no se pudo buscar).
	 */
	const applyRecentSearch = (search: RecentHotelSearch, submit = false) => {
		expandHotelForm.value = true
		window.scrollTo({ top: 0, behavior: 'smooth' })
		availabilityFormRef.value?.applyRecentSearch(search, { submit })
	}

	const handleRemoveTraveler = (travelerId: number) => {
		if (!travelerId) return

		if (availabilityFormRef.value) {
			availabilityFormRef.value.availabilityForm.travelers =
				availabilityFormRef.value.availabilityForm.travelers.filter(
					(t) => t.id !== travelerId
				)

			cleanupIfNoSpecificErrors()
		}
	}

	const handleClearError = (key: string, index: number) => {
		if (errorMessage.value.errors[key]) {
			errorMessage.value.errors[key].splice(index, 1)

			if (errorMessage.value.errors[key].length === 0) {
				delete errorMessage.value.errors[key]
			}

			cleanupIfNoSpecificErrors()
		}
	}

	const cleanupIfNoSpecificErrors = () => {
		if (!errorMessage.value?.errors) return

		const specificErrorKeys = Object.keys(errorMessage.value.errors).filter(
			(key) => key !== 'users'
		)
		const hasSpecificErrors = specificErrorKeys.some((key) => {
			const errorList = errorMessage.value.errors[key]

			return Array.isArray(errorList) && errorList.length > 0
		})

		if (!hasSpecificErrors) {
			errorMessage.value = null
		}
	}

	const cleanAllErrors = (travelerIds: number[]) => {
		if (errorMessage.value?.errors) {
			Object.keys(errorMessage.value.errors).forEach((key) => {
				errorMessage.value.errors[key] = []
			})

			travelerIds.forEach((id) => handleRemoveTraveler(id))
		}

		cleanupIfNoSpecificErrors()
	}

	watch(
		width,
		() => {
			defineClientType()
			evaluateLayout()
		},
		{ immediate: true }
	)

	watch(isFetching, (value) => {
		if (value) {
			expandHotelForm.value = false
		}
	})

	watch(
		displayHotels,
		() => {
			animateHotelList.value = false
			setTimeout(() => {
				animateHotelList.value = true
			}, 500)
		},
		{ immediate: true }
	)

	onBeforeUnmount(() => {
		resetValues()
		errorMessage.value = ''
	})

	watch(
		() => availabilityFormRef.value?.availabilityForm.member,
		(value, oldValue) => {
			if (!value && errorMessage.value?.errors) {
				errorMessage.value = null
			}
			if (oldValue !== undefined) {
				availabilityFormRef.value?.availabilityValidation.member.$touch()
			}
		},
		{ immediate: true }
	)

	watch(
		() => availabilityFormRef.value?.availabilityForm.travelers,
		(value, oldValue) => {
			if (oldValue !== undefined && value !== oldValue) {
				availabilityFormRef.value?.availabilityValidation.travelers.$touch()
			}
		},
		{ deep: true }
	)

	watch(
		() => availabilityFormRef.value?.availabilityForm.destination,
		(value, oldValue) => {
			if (oldValue !== undefined) {
				availabilityFormRef.value?.availabilityValidation.destination.$touch()
			}
		}
	)

	watch(
		() => availabilityFormRef.value?.availabilityForm.date,
		(value, oldValue) => {
			if (oldValue !== undefined && value !== oldValue) {
				availabilityFormRef.value?.availabilityValidation.date.$touch()
			}
		},
		{ deep: true }
	)

	provide('errorMessage', errorMessage)

	watch(hotels, (newHotels, oldHotels) => {
		if (newHotels.length > 0 && oldHotels.length === 0) {
			const hasFavorites = newHotels.some((h) => h.favorite)
			if (hasFavorites && !user.value?.internal) {
				setOrderBy(OrderConditions.Favorites as OrderConditions)
			} else {
				setOrderBy(OrderConditions.AscendingSort as OrderConditions)
			}
		}
	})

	const handleSuggestHotel = () => {
		if (hotelPreferences.value?.mode === 'restrict') return
		if (availabilityFormRef.value) {
			availabilityFormRef.value.createReservation()
		}
	}

	const policyObjectMap = computed(() => {
		return availabilityFormRef.value?.policyObjectMap ?? null
	})

	const daysInAdvance = computed(() => {
		const start = form.value.Start
		if (!start) return undefined
		const diff = Math.ceil(
			(new Date(start).getTime() - Date.now()) / 86_400_000
		)
		return diff >= 0 ? diff : 0
	})
</script>

<template>
	<section>
		<div ref="aboveMapRef">
			<BreadCrumb :pages="pages"></BreadCrumb>
			<h1 class="sr-only">Reservación hotelera</h1>

			<Transition>
				<TravelerConflictAlert
					v-if="errorMessage?.errors && hasSpecificErrors"
					:errors="errorMessage.errors"
					@remove-traveler="
						(travelerId) => {
							handleRemoveTraveler(travelerId)
						}
					"
					@clear-error="
						(key, i) => {
							handleClearError(key, i)
						}
					"
					@close-alert="
						(travelerIds) => {
							cleanAllErrors(travelerIds)
						}
					"
				/>
			</Transition>

			<AvailabilityForm
				:linked-cef-requests="linkedCefRequests"
				ref="availabilityFormRef"
				:expand-hotel-form="expandHotelForm"
				:linked-flight="
					(linkedFlight as FlightRequestDetail | null | undefined) ?? null
				"
				@change="setExpandHotelForm"
			/>

			<!-- Active Search Summary Bar -->
			<div
				v-if="!expandHotelForm"
				class="sticky top-16 z-10 w-full bg-white border-b-2 border-blue-500 shadow-md"
			>
				<AvailabilitySummarySearch
					:form="form"
					:selected-people="selectedPeople"
					:reservation-nights="reservationNights"
					:is-fetching="isFetching"
					:set-new-request="setNewRequest"
				/>
			</div>
		</div>

		<!-- isQuickSearching cubre la búsqueda disparada desde AvailabilitySummarySearch
		     vía otra instancia de useHotels(), por si su isFetching no llega a
		     sincronizarse con el de esta página. -->
		<LoadingHotels :loading="isFetching || isQuickSearching" />

		<div
			v-if="
				!expandHotelForm &&
				!isFetching &&
				!isQuickSearching &&
				hotelPreferences?.mode === 'restrict' &&
				filteredHotelList.length === 0
			"
			role="status"
			class="mx-auto mt-4 max-w-4xl rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-[#253C70]"
		>
			<p class="font-semibold">
				No hay hoteles autorizados que coincidan con esta búsqueda.
			</p>
			<p>
				Se aplicó la lista de
				{{ searchContext?.companyBranchId ? 'esta sede' : 'tu empresa' }}.
				Revisa las fechas y los filtros o consulta al administrador para ampliar
				las opciones permitidas.
			</p>
		</div>

		<!-- Hotel filters and information -->
		<Transition name="hotels">
			<div
				v-if="!expandHotelForm && !isFetching && !isQuickSearching && hotels"
				ref="listSectionRef"
				class="flex flex-col justify-around pt-5"
			>
				<!-- Hotels and map -->
				<div
					v-if="query === '' && hotels?.length > 0"
					ref="hotelListRef"
					:class="[
						'flex h-full rounded-md',
						displayMap && displayHotels && 'gap-4',
					]"
				>
					<!-- Hotels container -->
					<div
						:class="[
							'relative mx-auto flex-1 transition-all duration-300',
							displayHotels ? 'block' : 'hidden',
							displayMap && displayHotels ? '!max-w-md' : 'max-w-5xl',
						]"
					>
						<!-- Scroll to Top Button -->
						<button
							v-if="!displayMap && !extraInformationIsVisible"
							class="fixed bottom-6 right-6 z-20 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 border-2 border-white"
							:class="{ 'md:!bottom-10': isMobile }"
							@click="() => moveToList(hotelListRef)"
							v-tooltip="'Volver arriba'"
						>
							<span class="material-symbols-outlined text-2xl"
								>arrow_upward</span
							>
						</button>

						<div
							:class="[
								'max-w-full',
								displayMap ?
									'overflow-y-auto px-2 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100'
								:	'px-4',
							]"
							:style="
								displayMap ?
									// 5rem = header fijo de la app (no vive en esta página, así
									// que se queda como constante). aboveMapHeight sí se mide en
									// vivo: breadcrumb + form + barra de búsqueda colapsada no
									// tienen un alto fijo real (por ejemplo, disponible sin recorte
									// en desktop vs. wrap en mobile). Esta columna y la del mapa
									// están en el mismo flex row (align-items: stretch por
									// default), así que si solo se ajusta una, la otra la vuelve a
									// estirar al alto viejo — de ahí que ambas usen esta misma
									// altura (ver AvailabilityInteractiveInformation.vue).
									// 100dvh, no 100vh: en mobile (Chrome/Safari) 100vh mide el
									// viewport con la barra de direcciones colapsada (el más
									// grande) — con la barra visible el alto real es menor y el
									// body desborda, aunque en desktop o en un viewport simulado
									// sin esa barra no se note. 100dvh sigue el viewport visible.
									{ height: `calc(100dvh - 5rem - ${aboveMapHeight}px)` }
								:	undefined
							"
						>
							<div class="mx-auto flex max-w-4xl flex-col gap-2">
								<div
									v-if="policyInformation?.warnings?.length"
									class="max-w-4xl mx-auto w-full my-2"
								>
									<StyledAlertList
										type="Warning"
										title="Esta solicitud no cumple con las condiciones de algunas politicas; se le notificara a su superior en caso de continuar con una reserva."
										sub-title="Politicas infringidas:"
										:alerts="
											policyInformation.warnings.map(
												(warning) => warning.message
											)
										"
										:show-limit="2"
									/>
								</div>

								<!-- Results Control Bar -->
								<!-- `top-16` (altura del header) y no `top-40`: la barra de
								     resumen de la búsqueda vive dentro de `aboveMapRef`, que
								     cierra antes de esta lista. Un sticky sólo queda fijo
								     mientras su padre siga a la vista, así que al bajar por los
								     resultados esa barra se despega y deja de ocupar los 96px
								     que `top-40` reservaba — el contenedor quedaba flotando a
								     media pantalla con las tarjetas pasando por debajo. -->
								<div
									ref="extraInformationRef"
									class="sticky z-10 bg-white rounded-lg shadow-md border border-gray-200 p-3 mb-3"
									:class="[displayMap ? 'top-0' : 'max-w-4xl top-16']"
								>
									<div class="flex flex-col gap-4">
										<!-- Filters and Actions Row and search query input -->
										<AvailabilityFilters
											:is-fetching="isFetching"
											:display-map="displayMap"
											:expand-hotel-form="expandHotelForm"
											:order-by="typedOrderBy"
											:favorite-price-order="favoritePriceOrder"
											@set-favorite-price-order="setFavoritePriceOrder"
											@set-order-by="setOrderBy"
											@show-map="showMap"
											@set-new-request="setNewRequest"
										/>

										<!-- Legacy view toggle for mobile -->
										<ViewSectionToggle
											v-if="!displayHotels && width < 768"
											text="Ver hoteles"
											:toggle-sections="toggleSections"
											class-name="fixed right-1/2 bottom-6 z-[1] mx-auto flex min-w-[42px] max-w-xs translate-x-1/2 items-center justify-center gap-2 rounded-md border bg-white p-2 shadow-lg"
										/>

										<!-- Old mobile map button - hidden now -->
										<ViewSectionToggle
											v-if="!displayMap && width < 768"
											text="Ver mapa"
											:toggle-sections="toggleSections"
											:map-toggle="true"
											class="hidden"
										/>
									</div>
								</div>

								<!-- Con «ocultar», la empresa sólo deja ver sus hoteles preferidos: sin
								este aviso, una lista corta (o vacía) parece falta de disponibilidad. -->
								<p
									v-if="hotelPreferences"
									class="mb-3 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-[#021F58]"
								>
									<span class="material-symbols-outlined text-[18px]"
										>verified</span
									>
									<span>
										<template v-if="hotelPreferences.mode === 'restrict'">{{
											searchContext?.companyBranchId ?
												'Esta sede sólo permite los hoteles configurados.'
											:	'Tu empresa sólo permite sus hoteles preferidos.'
										}}</template>
										<template v-else
											>{{
												searchContext?.companyBranchId ?
													'Los hoteles preferidos para esta sede aparecen primero.'
												:	'Los hoteles preferidos de tu empresa aparecen primero.'
											}}
											Los demás también se muestran.</template
										>
										<template v-if="hotelPreferences.hidden > 0">
											{{ hotelPreferences.hidden }}
											{{
												hotelPreferences.hidden === 1 ?
													'hotel quedó oculto'
												:	'hoteles quedaron ocultos'
											}}
											en esta búsqueda.
										</template>
									</span>
								</p>

								<template v-if="filteredHotelList.length > 0">
									<!-- Hotels list with transition -->
									<TransitionGroup
										:name="animateHotelList ? 'hotel-list' : ''"
										class="flex flex-col gap-4 snap-y"
									>
										<ErrorBoundary
											v-for="hotel in filteredHotelList"
											:key="hotel.id"
										>
											<HotelEntry
												ref="itemRefs"
												:hotel="hotel"
												:is-mobile="isMobile"
												:display-map="displayMap"
												:selected-hotel="selectedHotel"
												:reservation-nights="reservationNights"
												:center-active-hotel="centerActiveHotel"
												:open-disclaimer-modal="openDisclaimerModal"
												:policy-object-map="policyObjectMap"
												:days-in-advance="daysInAdvance"
												@set-selected-hotel="setSelectedHotel"
												@push-to-details="pushToDetails"
												:isFetching="isFetching"
											/>
										</ErrorBoundary>
									</TransitionGroup>

									<div class="mb-20 mt-2 md:mb-10">
										<div
											ref="lastElement"
											class="mx-auto flex w-full"
										>
											<LoadMoreButton
												:is-fetching="isFetching"
												:more-indicator="moreIndicator"
												@search-hotels="searchHotels"
											/>
										</div>
									</div>

									<div
										v-if="
											user?.internal && form?.previousRequestData?.nombre_hotel
										"
										v-tooltip="'Información del ECR'"
										class="fixed inset-y-1/2 right-0 -translate-y-1/2 pr-4 z-50"
									>
										<Popover class="relative">
											<PopoverButton
												class="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-lg shadow-lg flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
											>
												<svg
													class="w-5 h-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
													></path>
												</svg>
												<span class="text-sm">ECR</span>
											</PopoverButton>

											<transition
												enter-active-class="transition duration-200 ease-out transform"
												enter-from-class="opacity-0 translate-y-2"
												enter-to-class="opacity-100 translate-y-0"
												leave-active-class="transition duration-150 ease-in transform"
												leave-from-class="opacity-100 translate-y-0"
												leave-to-class="opacity-0 translate-y-2"
											>
												<PopoverPanel
													class="absolute right-0 mt-2 w-72 bg-white border border-gray-200 shadow-xl rounded-lg p-4 text-gray-800"
												>
													<h3 class="text-lg font-semibold text-gray-900 mb-3">
														Información del ECR
													</h3>

													<div class="space-y-2 text-sm">
														<div class="flex justify-between">
															<span class="font-medium text-gray-600"
																>🏨 Hotel:</span
															>
															<span class="text-gray-900">{{
																form?.previousRequestData?.nombre_hotel
															}}</span>
														</div>
														<div class="border-t border-gray-200 my-1"></div>
														<div class="flex justify-between">
															<span class="font-medium text-gray-600"
																>📅 Inicio:</span
															>
															<span class="text-gray-900">{{
																form?.previousRequestData?.fecha_inicio
															}}</span>
														</div>
														<div class="flex justify-between">
															<span class="font-medium text-gray-600"
																>📅 Fin:</span
															>
															<span class="text-gray-900">{{
																form?.previousRequestData?.fecha_final
															}}</span>
														</div>
														<div class="border-t border-gray-200 my-1"></div>
														<div class="flex justify-between">
															<span class="font-medium text-gray-600"
																>👤 Huésped:</span
															>
															<span class="text-gray-900">{{
																form?.previousRequestData?.huesped
															}}</span>
														</div>
													</div>
												</PopoverPanel>
											</transition>
										</Popover>
									</div>
								</template>

								<NoSearchedHotelsMessage
									v-if="
										filteredHotelList.length === 0 &&
										hotelPreferences?.mode !== 'restrict'
									"
									:search-query="searchQuery"
									:more-indicator="moreIndicator"
								>
									<LoadMoreButton
										:is-fetching="isFetching"
										:more-indicator="moreIndicator"
										@search-hotels="searchHotels"
									/>
								</NoSearchedHotelsMessage>

								<!-- Sugerencia de Hotel Offline (Si ya finalizó la carga de búsqueda y se escribió un query) -->
								<div
									class="mt-6 mx-auto w-full max-w-4xl animate-fade-in"
									v-if="
										searchQuery !== '' && hotelPreferences?.mode !== 'restrict'
									"
								>
									<div
										class="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xs"
									>
										<div class="flex items-center gap-3">
											<div
												class="w-10 h-10 rounded-xl bg-[#253C70]/10 flex items-center justify-center text-[#253C70] flex-shrink-0"
											>
												<span class="material-symbols-outlined text-[20px]"
													>help_center</span
												>
											</div>
											<div class="text-center sm:text-left">
												<h4 class="text-sm font-semibold text-gray-900">
													¿No encuentras el hotel que buscas?
												</h4>
												<p class="text-xs text-gray-500 mt-0.5">
													Puedes sugerir o solicitar un hotel fuera de línea de
													forma manual.
												</p>
											</div>
										</div>
										<button
											type="button"
											class="bg-[#253C70] hover:bg-[#1a2b52] text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-all shadow-xs flex items-center gap-2 whitespace-nowrap"
											@click="handleSuggestHotel"
										>
											<span class="material-symbols-outlined text-sm"
												>edit_note</span
											>
											Sugiérelo aquí
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Map container -->
					<div
						v-if="displayMap"
						ref="mapRef"
						:class="[
							'relative flex-1',
							displayHotels && 'border-l border-gray-200 rounded-l-md',
						]"
					>
						<AvailabilityInteractiveInformation
							ref="interactiveInfoRef"
							v-model:display-map="displayMap"
							v-model:display-hotels="displayHotels"
							:availability-form-ref="availabilityFormRef"
							:extra-information-is-visible="extraInformationIsVisible"
							:hotel-list-ref="hotelListRef"
							:is-mobile="isMobile"
							:item-refs="itemRefs"
							:above-map-height="aboveMapHeight"
							@open-disclaimer-modal="openDisclaimerModal"
						/>
					</div>
				</div>

				<!-- Si la búsqueda falló (isError) o no trajo hoteles en absoluto (hotels?.length === 0) -->
				<div
					v-else-if="isError || hotels?.length === 0"
					class="mx-auto w-full max-w-4xl px-4 flex flex-col gap-6 items-center my-10 animate-fade-in"
				>
					<div
						class="py-12 md:px-16 max-w-2xl text-center mx-auto flex flex-col items-center gap-6 border border-gray-200 bg-white rounded-2xl shadow-xs px-6 w-full"
					>
						<div
							class="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-amber-500"
						>
							<span class="material-symbols-outlined text-2xl">info</span>
						</div>
						<div>
							<h3 class="font-bold text-lg md:text-xl text-gray-800">
								{{
									hotelPreferences?.mode === 'restrict' ?
										'No hay hoteles autorizados disponibles'
									:	'No hay hoteles disponibles en este destino'
								}}
							</h3>
							<p class="max-w-md text-sm text-gray-500 mt-2 mx-auto">
								{{
									hotelPreferences?.mode === 'restrict' ?
										'Se respetó la lista de hoteles permitidos. Revisa las fechas o consulta al administrador para ampliar las opciones autorizadas.'
									:	'No se han encontrado tarifas u hoteles registrados en este destino para las fechas seleccionadas.'
								}}
							</p>
						</div>
						<figure class="w-20 opacity-80">
							<img
								class="w-full"
								src="@/assets/imgs/lupa.png"
								alt="Lupa"
							/>
						</figure>
					</div>

					<!-- Alternativa offline sólo cuando la política de la empresa la permite. -->
					<div
						v-if="hotelPreferences?.mode !== 'restrict'"
						class="w-full"
					>
						<div
							class="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xs"
						>
							<div class="flex items-center gap-3">
								<div
									class="w-10 h-10 rounded-xl bg-[#253C70]/10 flex items-center justify-center text-[#253C70] flex-shrink-0"
								>
									<span class="material-symbols-outlined text-[20px]"
										>help_center</span
									>
								</div>
								<div class="text-center sm:text-left">
									<h4 class="text-sm font-semibold text-gray-900">
										¿No encuentras el hotel que buscas?
									</h4>
									<p class="text-xs text-gray-500 mt-0.5">
										Puedes sugerir o solicitar un hotel fuera de línea de forma
										manual.
									</p>
								</div>
							</div>
							<button
								type="button"
								class="bg-[#253C70] hover:bg-[#1a2b52] text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-all shadow-xs flex items-center gap-2 whitespace-nowrap"
								@click="handleSuggestHotel"
							>
								<span class="material-symbols-outlined text-sm">edit_note</span>
								Sugiérelo aquí
							</button>
						</div>
					</div>
				</div>
			</div>
		</Transition>

		<!-- Va después de los resultados para quedar justo bajo el formulario cuando está
		     abierto, y bajo el aviso de «no hay hoteles» cuando está colapsado. -->
		<RecentHotelSearches
			v-if="showRecentSearches"
			@fill="(search) => applyRecentSearch(search)"
			@search="(search) => applyRecentSearch(search, true)"
		/>

		<!-- Disclaimer modal -->
		<TransitionRoot
			appear
			:show="disclaimerIsOpen"
			as="template"
		>
			<AvailabilityModalDisclaimer
				:actual-disclaimer="actualDisclaimer"
				@close="closeModal"
			/>
		</TransitionRoot>
	</section>
</template>

<style scoped lang="css">
	input::-webkit-outer-spin-button,
	input::-webkit-inner- spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}
	/* Filters */
	.order-by {
		@apply !bg-white text-sm !text-black;
	}
	.edit-button {
		@apply flex items-center rounded-md border-2 border-transparent bg-blue-500 p-2 px-2 text-sm font-semibold text-white hover:border-blue-700 hover:bg-blue-700;
	}
	.disabled {
		@apply opacity-50;
	}
	/* Hotels list */
	.hotel-list-move {
		transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
	}
	.map-details-enter-active,
	.map-details-leave-active {
		transition: opacity 250ms ease-in-out;
	}
	.map-details-enter-from,
	.map-details-leave-to {
		opacity: 0;
	}
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 250ms ease-in-out;
	}
	.form-collapse {
		transition: height 300ms cubic-bezier(0.33, 1, 0.68, 1);
	}

	.hotels-enter-active,
	.hotels-leave-active {
		transition: opacity 0.5s ease;
	}
	.hotels-enter-from,
	.hotels-leave-to {
		opacity: 0;
	}

	.v-enter-active,
	.v-leave-active {
		transition: opacity 0.5s ease;
	}

	.v-enter-from,
	.v-leave-to {
		opacity: 0;
	}

	/* Hotel list item animations */
	.hotel-list-enter-active {
		transition: all 0.5s ease-out;
	}

	.hotel-list-leave-active {
		transition: all 0.3s ease-in;
		position: absolute;
	}

	.hotel-list-enter-from {
		opacity: 0;
		transform: translateY(30px);
	}

	.hotel-list-leave-to {
		opacity: 0;
		transform: translateX(-30px);
	}

	/* Smooth scroll behavior */
	html {
		scroll-behavior: smooth;
	}

	/* Button hover effects enhancement */
	button {
		-webkit-tap-highlight-color: transparent;
	}

	/* Custom scrollbar styles */
	.scrollbar-thin::-webkit-scrollbar {
		width: 8px;
	}

	.scrollbar-thin::-webkit-scrollbar-track {
		@apply rounded-full bg-gray-100;
	}

	.scrollbar-thin::-webkit-scrollbar-thumb {
		@apply rounded-full bg-gray-400 hover:bg-gray-500;
	}

	.scrollbar-thumb-gray-400::-webkit-scrollbar-thumb {
		@apply bg-gray-400;
	}

	.scrollbar-track-gray-100::-webkit-scrollbar-track {
		@apply bg-gray-100;
	}
</style>
